import { FC, useContext, useRef } from "react";
import { Link } from "react-router-dom";
import { NetworkPortfolioManagementProject } from "@app/network-portfolio-management/interfaces";
import { ArchivedDatePill } from "@app/network-portfolio-management/components/ArchivedDatePill";
import { ProjectDescriptionLine } from "@app/network-portfolio-management/components/ProjectDescriptionLine";
import { ProjectStatusDisplay } from "@app/network-portfolio-management/components/ProjectStatusDisplay";
import { ProjectTableSelectionContext } from "@app/network-portfolio-management/contexts/ProjectTableSelectionContext";
import { TableScrollContainerContext } from "@app/network-portfolio-management/contexts/TableScrollContainerContext";
import { MembershipToggleContext } from "@app/network-portfolio-management/hooks/useMembershipToggle";
import { useNpmActionMenuProps } from "@app/network-portfolio-management/hooks/useNpmActionMenuProps";
import { SharedProjectActionMenu } from "@app/projects/network-assessment/SharedProjectActionMenu";
import { aliasLabel } from "@app/network-portfolio-management/utils/projectTypeUtils";
import {
  formatCount,
  formatPercent,
  formatScore,
} from "@app/network-portfolio-management/utils/formatUtils";
import { formatDateCustom } from "@app/shared/formatDateTime";
import { QesIcon } from "@app/shared/icons/QESIcon";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { formatAccuracyBandPercent } from "@app/shared/utility/formatAccuracyBandPercent";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { useIsOverflow } from "@app/shared/qes-standards/utility/useIsOverflow";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { ProjectRunConfigurationType } from "@app/projects/enumerators";

export const DASH = "---";

export const DashCell: FC = () => (
  <span className="network-portfolio-management-project-table__cell--dash">{DASH}</span>
);

export const ActionMenuCell: FC<{ project: NetworkPortfolioManagementProject }> = ({ project }) => {
  const actionMenuProps = useNpmActionMenuProps(project);
  const scrollContainerRef = useContext(TableScrollContainerContext);
  return <SharedProjectActionMenu {...actionMenuProps} fixMenu dropupContainer={scrollContainerRef ?? undefined} />;
};

export const SelectCell: FC<{ project: NetworkPortfolioManagementProject }> = ({ project }) => {
  const { onToggleSelect, selectedProjectIds } = useContext(ProjectTableSelectionContext);
  return (
    <RoleRestricted allowedRoles={[Roles.QUEST_USER, Roles.CLIENT_ADMIN]}>
      <QESCheckbox
        checked={selectedProjectIds.has(project.projectId)}
        hideLabel
        label={`Select ${project.name}`}
        testId={`network-portfolio-management-project-table-checkbox-${project.projectId}`}
        onChange={() => onToggleSelect(project.projectId)}
      />
    </RoleRestricted>
  );
};

interface ProjectTitleCellProps {
  project: NetworkPortfolioManagementProject;
}

export const ProjectTitleCell: FC<ProjectTitleCellProps> = ({ project }) => {
  const nameRef = useRef<HTMLAnchorElement>(null);
  // Only overflowing (ellipsis-truncated) names get a tooltip — matches the showTooltipOnOverflow
  // pattern used by QESCheckbox/RadioButton, rather than always showing one regardless of fit.
  const isNameOverflowing = useIsOverflow(nameRef);

  return (
    <div className="network-portfolio-management-project-table__title">
      <ArchivedDatePill
        dateArchived={project.dateArchived}
        testId={`network-portfolio-management-project-table-archived-pill-${project.projectId}`}
      />
      <TooltipWrapper
        text={isNameOverflowing ? project.name : ""}
        tooltipWidth="260px"
        triggerWidth="100%"
      >
        <Link
          ref={nameRef}
          to={`/projects/${project.projectId}`}
          className="network-portfolio-management-project-table__title-name"
          data-testid={`network-portfolio-management-project-table-title-${project.projectId}`}
        >
          {project.name}
        </Link>
      </TooltipWrapper>
      <div className="network-portfolio-management-project-table__title-subtitle">
        <ProjectDescriptionLine
          lineOfBusiness={project.lineOfBusiness}
          projectType={project.projectType}
          testId={`network-portfolio-management-project-table-lob-${project.projectId}`}
        />
      </div>
    </div>
  );
};

interface ProjectStatusCellProps {
  project: NetworkPortfolioManagementProject;
}

export const ProjectStatusCell: FC<ProjectStatusCellProps> = ({ project }) =>
  project.status == null ? <DashCell /> : <ProjectStatusDisplay project={project} />;

interface DateCellProps {
  dateString: string | null;
}

export const DateCell: FC<DateCellProps> = ({ dateString }) =>
  dateString == null ? <DashCell /> : <span>{formatDateCustom(dateString, "M/D/YY")}</span>;

interface TemplateNameCellProps {
  templateName: string | null;
}

export const TemplateNameCell: FC<TemplateNameCellProps> = ({ templateName }) =>
  templateName == null ? <DashCell /> : <span>{templateName}</span>;

interface NumberCellProps {
  value: number | null;
}

export const NumberCell: FC<NumberCellProps> = ({ value }) =>
  value == null ? <DashCell /> : <span>{formatCount(value)}</span>;

interface PercentCellProps {
  value: number | null;
}

export const PercentCell: FC<PercentCellProps> = ({ value }) =>
  value == null ? <DashCell /> : <span>{formatPercent(value)}</span>;

// Rounds to the nearest whole percent (via formatAccuracyBandPercent), matching card view's
// AccuracyOverview display of the same Accuracy band fields — PercentCell's raw formatPercent
// would show full float precision instead.
export const BandPercentCell: FC<PercentCellProps> = ({ value }) =>
  value == null ? <DashCell /> : <span>{formatAccuracyBandPercent(value)}</span>;

interface ScoreCellProps {
  value: number | null;
}

// Rounds to one decimal via formatScore, matching AdequacyOverview's and AccuracyInsights'
// Index Score cells on the Card view — NumberCell's toLocaleString() would show full float
// precision instead.
export const ScoreCell: FC<ScoreCellProps> = ({ value }) =>
  value == null ? <DashCell /> : <span>{formatScore(value)}</span>;

export const DualMembershipCell: FC<{ project: NetworkPortfolioManagementProject }> = ({ project }) => {
  const { membership, setMembership, showMembershipToggle } = useContext(MembershipToggleContext);

  if (!showMembershipToggle) {
    return <DashCell />;
  }

  return (
    <QESSelect
      containerClassName="network-portfolio-management-project-table__membership-wrapper"
      hideLabel
      label="Membership"
      testId={`network-portfolio-management-project-table-membership-${project.projectId}`}
      value={membership}
      onChange={(e) => setMembership(e.target.value as ProjectRunConfigurationType)}
    >
      <option value={ProjectRunConfigurationType.DualMembershipPrimary}>
        {aliasLabel(project.primary?.memberSetAlias, "Primary")}
      </option>
      <option value={ProjectRunConfigurationType.DualMembershipSupplemental}>
        {aliasLabel(project.secondary?.memberSetAlias, "Secondary")}
      </option>
    </QESSelect>
  );
};

export const AdequacyIndexScoreCell: FC = () => {
  const { active } = useContext(MembershipToggleContext);
  return <ScoreCell value={active?.adequacyScore ?? null} />;
};

// Adequacy breakdown, matching the backend's NetworkPortfolioManagementMembershipResult fields.
export const AdequacyMetCountCell: FC = () => {
  const { active } = useContext(MembershipToggleContext);
  return <NumberCell value={active?.adequacyMetCount ?? null} />;
};

export const AdequacyMetPercentCell: FC = () => {
  const { active } = useContext(MembershipToggleContext);
  return <PercentCell value={active?.adequacyMetPercent ?? null} />;
};

export const AdequacyTotalGapCountCell: FC = () => {
  const { active } = useContext(MembershipToggleContext);
  return <NumberCell value={active?.adequacyTotalGapCount ?? null} />;
};

export const AdequacyTotalGapPercentCell: FC = () => {
  const { active } = useContext(MembershipToggleContext);
  return <PercentCell value={active?.adequacyTotalGapPercent ?? null} />;
};

export const AdequacyApprovedExceptionsCountCell: FC = () => {
  const { active } = useContext(MembershipToggleContext);
  return <NumberCell value={active?.adequacyApprovedExceptionsCount ?? null} />;
};

// Reuses the same countyCount field the card's meta line (ProjectDescriptionLine) already reads —
// not part of the Adequacy Overview breakdown (the card doesn't show this count there either), and
// not gated by project type: it renders whatever countyCount the active membership has, regardless
// of adequacy/accuracy/zip status.
export const CountiesCountCell: FC = () => {
  const { active } = useContext(MembershipToggleContext);
  return <NumberCell value={active?.countyCount ?? null} />;
};

// Zip-level projects only; elsewhere zipCodeCount is null, so NumberCell dashes.
export const ZipCodesCountCell: FC = () => {
  const { active } = useContext(MembershipToggleContext);
  return <NumberCell value={active?.zipCodeCount ?? null} />;
};

export const AccuracyIndexScoreCell: FC = () => {
  const { active } = useContext(MembershipToggleContext);
  return <ScoreCell value={active?.accuracyIndexScore ?? null} />;
};

// Accuracy breakdown, matching the backend's NetworkPortfolioManagementMembershipResult fields.
export const AccuracyAttestationRateCell: FC = () => {
  const { active } = useContext(MembershipToggleContext);
  return <PercentCell value={active?.accuracyAttestationRate ?? null} />;
};

export const AccuracyAccuratePercentCell: FC = () => {
  const { active } = useContext(MembershipToggleContext);
  return <BandPercentCell value={active?.accuracyAccuratePercent ?? null} />;
};

export const AccuracyInaccuratePercentCell: FC = () => {
  const { active } = useContext(MembershipToggleContext);
  return <BandPercentCell value={active?.accuracyInaccuratePercent ?? null} />;
};

export const AccuracySuspectPercentCell: FC = () => {
  const { active } = useContext(MembershipToggleContext);
  return <BandPercentCell value={active?.accuracySuspectPercent ?? null} />;
};

export const AccuracyEnrichedPercentCell: FC = () => {
  const { active } = useContext(MembershipToggleContext);
  return <BandPercentCell value={active?.accuracyEnrichedPercent ?? null} />;
};

export const AccuracySkippedPercentCell: FC = () => {
  const { active } = useContext(MembershipToggleContext);
  return <BandPercentCell value={active?.accuracySkippedPercent ?? null} />;
};

interface TagsCellProps {
  tags: Record<string, string[]>;
}

export const TagsCell: FC<TagsCellProps> = ({ tags }) => {
  const entries = Object.entries(tags);
  if (entries.length === 0) {
    return <DashCell />;
  }

  // Whether 1 or 2 chips actually fit in the column's available width can't be known up front —
  // it depends on tag/group name length and how much room the column has (which now varies with
  // flexGrow). Rather than guess a chip count and risk silently hiding overflow with no way to
  // recover it, always render every chip and let `&__tags-visible`'s flex-shrink + overflow:hidden
  // clip whatever doesn't fit, while the "…" tooltip trigger (flex-shrink: 0) never shrinks and
  // stays reachable regardless of how much got clipped — a pure-CSS "show what fits, always offer
  // the full list" pattern that needs no width measurement.
  const totalTagCount = entries.reduce((total, [, tagNames]) => total + tagNames.length, 0);
  const hasMoreThanOneTag = entries.length > 1 || totalTagCount > 1;

  return (
    <div className="network-portfolio-management-project-table__tags">
      <div className="network-portfolio-management-project-table__tags-visible">
        {entries.map(([groupName, tagNames]) => (
          <span key={groupName} className="network-portfolio-management-project-table__tag">
            {groupName}
            <QesIcon iconName="Chevron Right Double" isDecorative className="align-text-top mlr5" />
            {tagNames.join(", ")}
          </span>
        ))}
      </div>
      {hasMoreThanOneTag && (
        <TooltipWrapper
          // TooltipWrapper defaults to width: "auto" on an absolutely-positioned inline-block with
          // word-break: break-word — for block content like this <ul>, browsers commonly resolve
          // that combination to a near-minimum shrink-to-fit width (wrapping almost every word)
          // rather than sizing to the content's natural width. A fixed width sidesteps that; 260px
          // matches this codebase's existing tooltip-width convention (see .velvet-tooltip, 250px).
          tooltipWidth="260px"
          // The Tags column is the table's rightmost, so TooltipWrapper's default centered-on-trigger
          // positioning puts roughly half the tooltip's width past the page's right edge. This class
          // (see _project-table.scss) overrides the transform to anchor from the trigger's right side
          // instead, and left-aligns the list text (centered reads oddly for a list).
          tooltipBodyClassName="network-portfolio-management-project-table__tags-tooltip"
          text={
            <ul className="network-portfolio-management-project-table__tags-tooltip-list">
              {entries.map(([groupName, tagNames]) => (
                <li key={groupName}>
                  {groupName} &gt;&gt; {tagNames.join(", ")}
                </li>
              ))}
            </ul>
          }
        >
          <span className="network-portfolio-management-project-table__tags-overflow">…</span>
        </TooltipWrapper>
      )}
    </div>
  );
};
